import {
  DEFAULT_FLEXIBLE_PROPERTIES_VIEW_MODE,
  FLEXIBLE_PROPERTIES_VIEW_MODE,
  type FlexiblePropertiesViewMode,
} from '../constants/FlexiblePropertiesConstants';
import QueryConstants from '../constants/QueryConstants';
import { ROUTES } from '../constants/urls';

import type { NextRouter } from 'next/router';

export type FlexiblePropertiesFilterState = {
  searchText: string;
  includeArchived: boolean;
};

export const defaultFlexiblePropertiesFilters: FlexiblePropertiesFilterState = {
  searchText: '',
  includeArchived: false,
};

const isValidViewMode = (value: string): value is FlexiblePropertiesViewMode =>
  Object.values(FLEXIBLE_PROPERTIES_VIEW_MODE).includes(value as FlexiblePropertiesViewMode);

export const initializeFlexiblePropertiesFiltersFromRouter = (
  query: NextRouter['query'],
  defaults: FlexiblePropertiesFilterState = defaultFlexiblePropertiesFilters,
): FlexiblePropertiesFilterState => ({
  ...defaults,
  searchText: (query[QueryConstants.SEARCH_TEXT] as string) || defaults.searchText,
  includeArchived: query[QueryConstants.ARCHIVED] === 'true' || defaults.includeArchived,
});

export const initializeViewModeFromRouter = (
  query: NextRouter['query'],
  defaultViewMode: FlexiblePropertiesViewMode = DEFAULT_FLEXIBLE_PROPERTIES_VIEW_MODE,
): FlexiblePropertiesViewMode =>
  isValidViewMode(query[QueryConstants.VIEW_MODE] as string)
    ? (query[QueryConstants.VIEW_MODE] as FlexiblePropertiesViewMode)
    : defaultViewMode;

export const initializeFlexiblePropertiesPageStateFromQuery = (query: NextRouter['query']) => ({
  filters: initializeFlexiblePropertiesFiltersFromRouter(query),
  viewMode: initializeViewModeFromRouter(query),
});

export const updateFlexibleRouterWithFilters = (
  router: NextRouter,
  filters: FlexiblePropertiesFilterState,
  viewMode: FlexiblePropertiesViewMode,
) => {
  const query = { ...router.query };
  let hasChanges = false;

  const updates: Record<string, string | undefined> = {
    [QueryConstants.SEARCH_TEXT]: filters.searchText.trim() || undefined,
    [QueryConstants.ARCHIVED]: filters.includeArchived ? 'true' : undefined,
    [QueryConstants.VIEW_MODE]: viewMode !== DEFAULT_FLEXIBLE_PROPERTIES_VIEW_MODE ? viewMode : undefined,
  };

  for (const [key, value] of Object.entries(updates)) {
    if (value !== undefined) {
      if (query[key] !== value) {
        query[key] = value;
        hasChanges = true;
      }
    } else if (query[key] !== undefined) {
      delete query[key];
      hasChanges = true;
    }
  }

  if (hasChanges) {
    router.replace({ pathname: ROUTES.PROPERTIES, query }, undefined, { shallow: true });
  }
};

const EXPANDED_GROUPS_KEY = 'zrm_flexible_properties_expanded_groups';

// Session-scoped: survives a refresh or a trip to a detail page and back, but a new tab starts collapsed.
export const getPersistedExpandedGroups = (): Array<string> => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(EXPANDED_GROUPS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : [];
  } catch {
    return [];
  }
};

export const persistExpandedGroups = (groupIds: Array<string>): void => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(EXPANDED_GROUPS_KEY, JSON.stringify(groupIds));
  } catch {
    // Silently fail if sessionStorage is unavailable
  }
};
